/* eslint-disable no-return-assign */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState, useContext, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';

import {
  TextInput, Select, TextArea, Datepicker, Form, Button, PhoneInput,
} from 'components/common';
import { UserContext } from 'contexts/userContext';
import { getSource } from 'models/source';
import { getObjectByLanguage } from 'language/helpers';
import { StatusContext } from 'contexts/statusContext';
import { updateUser } from 'models/user';
import { withTranslation } from 'language/i18n';
import Layout from 'components/partials/Layout';
import {
  emailValidator, minLengthValidator, nameValidator, nonRequiredNameValidator,
} from 'utils/formValidators';
import { GENDER } from 'constants/common';
import { turkishPhone } from 'utils/phoneFormatters';
import DisableAccountModal from 'components/DisableAccountModal';
import EditableListItem from './components/EditableListItem';
import ProfileContainer from './components/ProfileContainer';

const Profile = ({ t }) => {
  const { userData } = useContext(UserContext);
  const { setErrorState } = useContext(StatusContext);

  const user = userData;
  let formRef = useRef();

  const [editing, setEditing] = useState(false);
  const [onResponse, setOnResponse] = useState(false);

  const [name, setName] = useState(user.name);
  const [surname, setSurname] = useState(user.surname);
  const [country, setCountry] = useState(user.country);
  const [email, setEmail] = useState(user.email);
  const [identification, setIdentification] = useState(user.identification || '');
  const [passportNumber, setPassportNumber] = useState(user.passportNumber || '');
  const [phone, setPhone] = useState(user.phone);
  const [birthdate, setBirthdate] = useState(user.birthdate || '');
  const [landPhone, setLandPhone] = useState(user.landPhone || ''); // yok
  const [gender, setGender] = useState(user.gender || '');
  const [fax, setFax] = useState(user.fax || ''); // yok
  const [isUserFromTr, setIsUserFromTr] = useState(false);

  const [companyTitle, setCompanyTitle] = useState(user.companyTitle || '');
  const [companyEmergencyContactName, setCompanyEmergencyContactName] = useState(user.emergencyContactName || '');
  const [companyEmergencyContactSurname, setCompanyEmergencyContactSurname] = useState(user.emergencyContactSurname || '');
  const [companyEmergencyContactTitle, setCompanyEmergencyContactTitle] = useState(user.emergencyContactTitle || '');
  const [companyEmergencyContactEmail, setCompanyEmergencyContactEmail] = useState(user.emergencyContactEmail || '');
  const [companyEmergencyContactPhone, setCompanyEmergencyContactPhone] = useState(user.emergencyContactPhone || '');
  const [companyFax, setCompanyFax] = useState(user.companyFax || '');
  const [companyTaxOffice, setCompanyTaxOffice] = useState(user.companyTaxOffice || '');
  const [companyTaxNo, setCompanyTaxNo] = useState(user.companyTaxNo || '');
  const [companyAddress, setCompanyAddress] = useState(user.companyOfficeAddress || '');
  const [companySector, setCompanySector] = useState(user.companySector || '');
  const [companyLandPhone, setCompanyLandPhone] = useState(user.companyLandPhone || ''); // yok

  const [countryList, setCountryList] = useState([]);
  const [taxOfficeList, setTaxOfficeList] = useState([]);
  const [sectorList, setSectorList] = useState([]);

  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    getSources();
    formRef.setFieldsValue({
      name: user.name,
      surname: user.surname,
      country: user.country,
      email: user.email,
      identification: user.identification,
      passportNumber: user.passportNumber,
      phone: user.phone,
      birthdate: user.birthdate,
      landPhone: user.landPhone,
      gender: user.gender,
      fax: user.fax,
      isUserFromTr: user.isUserFromTr,
      companyTitle: user.companyTitle,
      companyEmergencyContactName: user.emergencyContactName,
      companyEmergencyContactSurname: user.emergencyContactSurname,
      companyEmergencyContactTitle: user.emergencyContactTitle,
      companyEmergencyContactEmail: user.emergencyContactEmail ? user.emergencyContactEmail : '',
      companyEmergencyContactPhone: user.emergencyContactPhone,
      companyFax: user.companyFax,
      companyTaxOffice: user.companyTaxOffice,
      companyTaxNo: user.companyTaxNo,
      companyOfficeAddress: user.companyOfficeAddress,
      companySector: user.companySector,
      companyLandPhone: user.companyLandPhone,
    });
  }, []);

  useEffect(() => {
    const userFrom = _.find(countryList, ['value', country]);
    if (userFrom) {
      setIsUserFromTr(userFrom.dialCode === '90');
    }
  }, [country]);

  const getSources = async () => {
    try {
      const { source: countries } = await getSource('country');
      const { source: taxOffices } = await getSource('taxOffice');
      const { source: sectors } = await getSource('sector');
      let fromTr = false;

      const manipulatedCountries = _.map(countries, (mapCountry) => {
        if (country === mapCountry.id) {
          fromTr = mapCountry.dialCode === '90';
        }
        return { value: mapCountry.id, text: getObjectByLanguage(mapCountry.name), dialCode: mapCountry.dialCode };
      });

      const manipulatedTaxOffices = _.map(taxOffices, (mapTaxOffice) => {
        return { value: mapTaxOffice.id, text: mapTaxOffice.name };
      });

      const manipulatedSector = _.map(sectors, (sector) => {
        return { value: sector.id, text: getObjectByLanguage(sector.name) };
      });

      setCountryList(manipulatedCountries);
      setTaxOfficeList(manipulatedTaxOffices);
      setSectorList(manipulatedSector);
      setIsUserFromTr(fromTr);
    } catch (error) {
      console.log('getSourcesErr', error);
    }
  };

  const findSelectText = (data, field) => {
    const foundedData = _.find(data, ['value', field]);
    if (foundedData) {
      return foundedData.text;
    }
    return '';
  };

  const onFormSubmit = async () => {
    if (!onResponse) {
      try {
        setOnResponse(true);
        await updateUser({
          name,
          surname,
          country,
          email,
          identification,
          passportNumber,
          phone,
          birthdate,
          landPhone,
          gender,
          fax,
          companyTitle,
          companyEmergencyContactName,
          companyEmergencyContactSurname,
          companyEmergencyContactTitle,
          companyEmergencyContactEmail,
          companyEmergencyContactPhone,
          companyFax,
          companyTaxOffice,
          companyTaxNo,
          companyAddress,
          companySector,
        });
        setEditing(false);
      } catch (error) {
        console.log('updateUserErr', error);
        if (error.status) {
          setErrorState({
            title: t('alert'),
            text: error.message,
            buttonText: t('close'),
            onClickButton: () => setErrorState({}),
          });
        }
      } finally {
        setOnResponse(false);
      }
    }
  };

  return (
    <ProfileContainer currentStep="profile">
      <div className="col-md-10 col-lg-12 mt-3 mt-md-0 profile-page">
        <Form
          layout="vertical"
          onFinish={onFormSubmit}
          formRef={(ref) => formRef = ref}
        >
          <div className="row align-items-stretch">
            <div className="col-15">
              <div className="im-box im-box--border-radius im-box--padding-t20-10 im-box--mh-100 d-flex flex-column">
                <div className="d-flex flex-row align-items-center justify-content-between mb-30">
                  <h2 className="fs-21 fw-900">{t('myProfile')}</h2>
                  {
                    editing
                      ? (
                        <div className="col-auto">
                          <Button
                            htmlType="submit"
                            className="im-button im-button--outline-black im-button--size-30 im-button--center"
                            label={t('save')}
                            loading={onResponse}
                          />
                        </div>
                      )
                      : (
                        <button
                          type="button"
                          className="im-button im-button--auto-height im-button--no-gutter im-button--transparent"
                          onClick={() => setEditing(true)}
                        >
                          <div className="im-button__icon im-button__icon--left fs-11">
                            <i className="fa-solid fa-pencil" />
                          </div>
                          <div className="fs-11">
                            <a>{t('edit')}</a>
                          </div>
                        </button>
                      )
                  }

                </div>

                <div className="im-box im-box--white im-box--border-radius im-box--padding-0 flex-grow-1">
                  <div className="p-3 d-flex justify-content-between align-items-center">
                    <div className="fs-15 fw-900 mb-1">Kişisel Bilgiler</div>
                  </div>
                  <div className="pl-3 pr-3">
                    <div className="separator separator--horizontal mb-2" />
                  </div>
                  <div className="p-3">
                    <div className="row">
                      <div className="col-15 col-lg">
                        <ul className="im-list im-list--type2">
                          <EditableListItem
                            title={t('name')}
                            value={name}
                            editing={editing}
                            input={(
                              <TextInput
                                name="name"
                                value={name}
                                defaultValue={name}
                                onChange={({ value }) => setName(value)}
                                required={_.isEmpty(name)}
                                validator={nameValidator}
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('nationality')}
                            value={findSelectText(countryList, country)}
                            editing={editing}
                            input={(
                              <Select
                                name="country"
                                onChange={({ value }) => setCountry(value)}
                                value={country}
                                defaultValue={country}
                                options={countryList}
                                showSearch
                                filterOption={(input, opt) => opt.children.toLocaleLowerCase('tr').indexOf(input.toLocaleLowerCase('tr')) >= 0}
                                required={_.isEmpty(country)}
                              />
                            )}
                          />
                          {
                            isUserFromTr
                              ? (
                                <EditableListItem
                                  title={t('identification')}
                                  value={identification}
                                  editing={editing}
                                  input={(
                                    <TextInput
                                      type="tel"
                                      id="only-number"
                                      name="identification"
                                      maxLength={11}
                                      value={identification}
                                      defaultValue={identification}
                                      onChange={({ value }) => {
                                        const fixedInput = value.replace(/[^0-9]/g, '');
                                        formRef.setFieldsValue({
                                          identification: fixedInput,
                                        });
                                        setIdentification(fixedInput);
                                      }}
                                      validator={(rule, value) => minLengthValidator(value, 11)}
                                    />
                                  )}
                                />
                              )
                              : (
                                <EditableListItem
                                  title={t('passportNumber')}
                                  value={passportNumber}
                                  editing={editing}
                                  input={(
                                    <TextInput
                                      type="text"
                                      id="only-number"
                                      name="passportNumber"
                                      maxLength={9}
                                      value={passportNumber}
                                      defaultValue={passportNumber}
                                      onChange={({ value }) => {
                                        const fixedInput = value.toLocaleUpperCase().replace(/[^0-9A-Z]/g, '');
                                        formRef.setFieldsValue({
                                          passportNumber: fixedInput,
                                        });
                                        setPassportNumber(fixedInput);
                                      }}
                                      validator={(rule, value) => minLengthValidator(value, 7)}
                                    />
                                  )}
                                />
                              )
                          }
                          <EditableListItem
                            title={t('birthdate')}
                            value={birthdate && moment(birthdate).format('DD/MM/YYYY')}
                            editing={editing}
                            input={(
                              <Datepicker
                                name="birthdate"
                                value={birthdate && moment(birthdate)}
                                defaultValue={birthdate && moment(birthdate)}
                                onChange={({ value }) => {
                                  const formattedValue = value ? moment(value, 'DD/MM/YYYY').toISOString() : '';
                                  setBirthdate(formattedValue);
                                }}
                                disabledDate={(current) => {
                                  // Disable dates that are less than 18 years ago (users must be at least 18 years old)
                                  return current && current > moment().subtract(18, 'years').endOf('day');
                                }}
                                format="DD/MM/YYYY"
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('gender')}
                            value={!_.isEmpty(gender) ? t(gender.toLowerCase()) : ''}
                            editing={editing}
                            input={(
                              <Select
                                name="gender"
                                onChange={({ value }) => setGender(value)}
                                value={gender}
                                defaultValue={gender}
                                options={[
                                  { value: GENDER.MALE, text: t('male') },
                                  { value: GENDER.FEMALE, text: t('female') },
                                ]}
                              />
                            )}
                          />
                        </ul>
                      </div>
                      <div className="col-15 col-lg">
                        <ul className="im-list im-list--type2">
                          <EditableListItem
                            title={t('surname')}
                            value={surname}
                            editing={editing}
                            input={(
                              <TextInput
                                name="surname"
                                value={surname}
                                defaultValue={surname}
                                onChange={({ value }) => setSurname(value)}
                                required={_.isEmpty(surname)}
                                validator={nameValidator}
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('email')}
                            value={email}
                            editing={editing}
                            input={(
                              <TextInput
                                disabled
                                name="email"
                                type="email"
                                value={email}
                                defaultValue={email}
                                onChange={({ value }) => setEmail(value)}
                                validator={emailValidator}
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('cellPhone')}
                            value={turkishPhone(phone)}
                            editing={editing}
                            input={(
                              <PhoneInput
                                name="phone"
                                value={phone}
                                onChange={({ value }) => setPhone(value)}
                                containerStyle={{ width: '100%' }}
                                required
                                mobileNumber
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('landPhone')}
                            value={landPhone}
                            editing={editing}
                            input={(
                              <PhoneInput
                                name="landPhone"
                                value={landPhone}
                                onChange={({ value }) => setLandPhone(value)}
                                containerStyle={{ width: '100%' }}
                                required={landPhone?.length > 2}
                              />
                            )}
                          />
                          <EditableListItem
                            title="Fax"
                            value={fax}
                            editing={editing}
                            input={(
                              <PhoneInput
                                name="fax"
                                value={fax}
                                onChange={({ value }) => setFax(value)}
                                containerStyle={{ width: '100%' }}
                                required={fax?.length > 2}
                              />
                            )}
                          />
                          {/* <EditableListItem
                            title={t('password')}
                            value={password}
                            editing={editing}
                            input={(
                              <TextInput
                                name="password"
                                value={password}
                                defaultValue={password}
                                onChange={({ value }) => setPassword(value)}
                              />
                            )}
                          /> */}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="im-box im-box--white im-box--border-radius im-box--padding-0 flex-grow-1 mt-3">
                  <div className="p-3">
                    <div className="fs-15 fw-900 mb-1">Firma Bilgileri</div>
                  </div>
                  <div className="pl-3 pr-3">
                    <div className="separator separator--horizontal mb-2" />
                  </div>
                  <div className="p-3">
                    <div className="row">
                      <div className="col-15 col-lg">
                        <ul className="im-list im-list--type2">
                          <EditableListItem
                            title={t('companyName')}
                            value={companyTitle}
                            editing={editing}
                            input={(
                              <TextInput
                                name="companyTitle"
                                value={companyTitle}
                                defaultValue={companyTitle}
                                onChange={({ value }) => setCompanyTitle(value)}
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('companyManagerName')}
                            value={companyEmergencyContactName}
                            editing={editing}
                            input={(
                              <TextInput
                                name="companyEmergencyContactName"
                                value={companyEmergencyContactName}
                                defaultValue={companyEmergencyContactName}
                                onChange={({ value }) => setCompanyEmergencyContactName(value)}
                                validator={nonRequiredNameValidator}
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('sector')}
                            value={findSelectText(sectorList, companySector)}
                            editing={editing}
                            input={(
                              <Select
                                name="companySector"
                                onChange={({ value }) => setCompanySector(value)}
                                value={companySector}
                                defaultValue={companySector}
                                options={sectorList}
                                showSearch
                                filterOption={(input, opt) => opt.children.toLocaleLowerCase('tr').indexOf(input.toLocaleLowerCase('tr')) >= 0}
                                required={!companySector}
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('taxNumber')}
                            value={companyTaxNo}
                            editing={editing}
                            input={(
                              <TextInput
                                type="text"
                                id="only-number"
                                name="companyTaxNo"
                                maxLength={10}
                                value={companyTaxNo}
                                defaultValue={companyTaxNo}
                                onChange={({ value }) => {
                                  const fixedInput = value.toLocaleUpperCase().replace(/[^0-9]/g, '');
                                  formRef.setFieldsValue({
                                    companyTaxNo: fixedInput,
                                  });
                                  setCompanyTaxNo(fixedInput);
                                }}
                                validator={(rule, value) => minLengthValidator(value, 10)}
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('taxOffice')}
                            value={findSelectText(taxOfficeList, companyTaxOffice)}
                            editing={editing}
                            input={(
                              <Select
                                name="companyTaxOffice"
                                onChange={({ value }) => setCompanyTaxOffice(value)}
                                value={companyTaxOffice}
                                defaultValue={companyTaxOffice}
                                options={taxOfficeList}
                                showSearch
                                filterOption={(input, opt) => opt.children.toLocaleLowerCase('tr').indexOf(input.toLocaleLowerCase('tr')) >= 0}
                                required={!companyTaxOffice}
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('address')}
                            value={companyAddress}
                            editing={editing}
                            input={(
                              <TextArea
                                name="companyAddress"
                                value={companyAddress}
                                defaultValue={companyAddress}
                                onChange={({ value }) => setCompanyAddress(value)}
                                autoSize={{ minRows: 4, maxRows: 5 }}
                              />
                            )}
                          />
                        </ul>
                      </div>
                      <div className="col-15 col-lg">
                        <ul className="im-list im-list--type2">
                          <EditableListItem
                            title={t('companyManagerTitle')}
                            value={companyEmergencyContactTitle}
                            editing={editing}
                            input={(
                              <TextInput
                                name="companyEmergencyContactTitle"
                                value={companyEmergencyContactTitle}
                                defaultValue={companyEmergencyContactTitle}
                                onChange={({ value }) => setCompanyEmergencyContactTitle(value)}
                                validator={nonRequiredNameValidator}
                              />
                            )}
                          />

                          <EditableListItem
                            title={t('companyManagerSurname')}
                            value={companyEmergencyContactSurname}
                            editing={editing}
                            input={(
                              <TextInput
                                name="companyEmergencyContactSurname"
                                value={companyEmergencyContactSurname}
                                defaultValue={companyEmergencyContactSurname}
                                onChange={({ value }) => setCompanyEmergencyContactSurname(value)}
                                validator={nonRequiredNameValidator}
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('email')}
                            value={companyEmergencyContactEmail}
                            editing={editing}
                            input={(
                              <TextInput
                                type="email"
                                name="companyEmergencyContactEmail"
                                value={companyEmergencyContactEmail}
                                defaultValue={companyEmergencyContactEmail}
                                onChange={({ value }) => setCompanyEmergencyContactEmail(value)}
                                validator={!_.isEmpty(companyEmergencyContactEmail) && emailValidator}
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('cellPhone')}
                            value={companyEmergencyContactPhone}
                            editing={editing}
                            input={(
                              <PhoneInput
                                name="companyEmergencyContactPhone"
                                value={companyEmergencyContactPhone}
                                onChange={({ value }) => value.length > 2 && setCompanyEmergencyContactPhone(value)}
                                containerStyle={{ width: '100%' }}
                                required={companyEmergencyContactPhone?.length > 2}
                                mobileNumber
                              />
                            )}
                          />
                          <EditableListItem
                            title={t('landPhone')}
                            value={companyLandPhone}
                            editing={editing}
                            input={(
                              <PhoneInput
                                name="companyLandPhone"
                                value={companyLandPhone}
                                onChange={({ value }) => setCompanyLandPhone(value)}
                                containerStyle={{ width: '100%' }}
                                required={companyLandPhone?.length > 2}
                              />
                            )}
                          />
                          <EditableListItem
                            title="Fax"
                            value={companyFax}
                            editing={editing}
                            input={(
                              <PhoneInput
                                name="companyFax"
                                value={companyFax}
                                onChange={({ value }) => setCompanyFax(value)}
                                containerStyle={{ width: '100%' }}
                                required={companyFax?.length > 2}
                              />
                            )}
                          />
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-30 text-center">
                  {editing && (
                    <a
                      onClick={() => setModalVisible(true)}
                      className="im-button im-button--transparent"
                    >
                      <span className="text-red1 fw-500">{t('deleteAccount')}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Form>
        <DisableAccountModal
          visible={modalVisible}
          setVisible={setModalVisible}
        />
      </div>
    </ProfileContainer>
  );
};

Profile.propTypes = {
  t: PropTypes.func,
};

export const getServerSideProps = async ({ req }) => {
  const { t } = req;

  return {
    props: {
      blackHeader: true,
      shortFooter: true,
      headTitle: t('profile'),
      hoc: true,
    },
  };
};

Profile.Layout = Layout;

export default withTranslation('common')(Profile);
